<template>
  <div
    class="page flex h-full min-h-[60vh] flex-col items-stretch justify-start xl:min-h-[100dvh]"
    :class="pending ? 'animate-pulse bg-slate-600 bg-opacity-75' : ''"
  >
    <header
      class="page-header img aspect-[4/3] max-h-[500px] w-full overflow-clip transition-all"
      :class="pending ? 'animate-pulse bg-slate-600 bg-opacity-75' : ''"
    >
      <img
        v-if="data?.image?.src"
        class="primary-image h-full w-full object-cover object-center"
        :src="data.image.src"
      />
    </header>
    <div class="page-main container h-full flex-grow">
      <template v-if="!useNativeAudio">
        <div v-if="canAccessDailyWord" class="relative">
          <DailyWordAudio
            v-if="audio"
            class="absolute right-0 isolate -mt-[2rem]"
            :disabled="!canAccessDailyWord"
            :src="audio.src"
            :type="audio.type"
          />
        </div>
      </template>

      <div class="dw mx-auto max-w-[65ch] space-y-8 pt-9 pb-8">
        <div class="">
          <div
            class="flex flex-col gap-x-4 mb-4 text-md uppercase leading-tight xs:flex-row sm:justify-between sm:max-w-[34rem] md:max-w-[40rem] lg:max-w-full"
          >
            <h4 class="page-date">
              <NuxtLink
                class="inline-flex justify-start items-center text-brand-peacock font-semibold underline hover:no-underline dark:text-brand-cornflower"
                :to="
                  localePath({
                    path: '/daily-word/pick',
                    query: { date: dateString },
                  })
                "
              >
                <Icon name="mdi:calendar-select" mode="svg" class="text-xl" />
                <span class="label">{{ formattedDate }}</span>
              </NuxtLink>
            </h4>
            <FavoritesToggle
              v-if="data"
              :uuid="data.uuid"
              :title="data.title"
              :date="date"
              :path="canonicalPath"
            />
          </div>
          <h4
            class="page-subtitle text-sm uppercase leading-tight text-gray-600 dark:text-[#A4ADBB]"
          >
            <span
              :class="
                pending
                  ? 'inline-block bg-slate-600 bg-clip-content text-slate-600 opacity-60'
                  : ''
              "
            >
              {{ t('Daily Word') }}:
            </span>
          </h4>
          <h1 class="page-title font-serif text-5xl font-bold leading-tight">
            <span
              v-if="pending"
              class="inline-block w-[15ch] bg-slate-600 bg-clip-content py-2 text-slate-600 opacity-60"
            >
              &nbsp;
            </span>
            <template v-else>
              {{ data?.title }}
            </template>
          </h1>
        </div>
        <hr class="sep" />
        <div
          v-if="pending"
          class="h-[25vh] w-full bg-slate-600 bg-clip-content text-slate-600 opacity-60"
        ></div>
        <DailyWordContent
          v-else-if="canAccessDailyWord"
          :affirmation="data?.subtitle"
          :body="data?.body?.markup"
          :audio="audio"
        />
        <DailyWordPayWall v-else />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import mime from 'mime'
import { parseFilename } from 'ufo'
import { fromDateString, toDateString, isDateString } from '~/utils/date'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()

const { canAccessDailyWord } = useLoginLinks()
const { useNativeAudio } = usePreferences()

const today = () => toDateString(new Date())

const dateString = (
  isDateString(route.params.date as string) ? route.params.date : today()
) as string

const canonicalPath = computed(() => `/daily-word/${dateString}`)

const date = computed(() => fromDateString(dateString))
const formattedDate = computed(() => {
  const lang = unref(locale)
  const fmt = lang === 'es' ? 'D MMMM YYYY' : 'MMMM D, YYYY'
  return toDateString(date.value, fmt, lang)
})

const _isToday = computed(() => dateString === today())

const { data, status, error } = await useFetch(
  `/api/data/daily-message/${dateString}/full`,
  {
    key: `daily-message-${dateString}`,
    query: { lang: locale.value },
  },
)

if (error.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
    cause: 'page-not-found',
    data: unref(error),
    fatal: true,
  })
}

const pending = computed(() => status.value === 'pending')

const audio = computed(() => {
  let src = unref(data)?.audio
  // @todo: FIX: sometimes the audio is 'null' instead of undefined
  if (src === 'null') src = undefined
  if (!src) return

  const filename = parseFilename(src, { strict: true })
  const ext = (filename?.split('.') || ['mp3']).at(-1) as string
  const type = mime.getType(ext)

  return {
    src,
    type: type || undefined,
  }
})

const dw = t('Daily Word')

useHead({
  title: unref(data)?.title || dw,
})

const description = computed(() => unref(data)?.subtitle || '')
const image = computed(() => unref(data)?.image?.src)

useSeoMeta({
  description: description,
  ogTitle: unref(data)?.title || dw,
  ogDescription: description,
  ogImage: image,
  ogUrl: route.fullPath,
  twitterTitle: unref(data)?.title || dw,
  twitterDescription: description,
  twitterImage: image,
  twitterCard: 'summary',
})

const setI18nParams = useSetI18nParams()
setI18nParams({
  en: { slug: 'daily-word' },
  es: { slug: 'la-palabra-diaria' },
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/daily-word/[date]',
    es: '/la-palabra-diaria/[date]',
  },
})

definePageMeta({
  validate(route) {
    const date = route.params.date as string

    if (date === 'today') return true
    // Check if the date param is valid
    return !isNaN(Date.parse(date))
  },
})
</script>
